import * as React from 'react';
import { connect } from 'react-redux';
import { camelCase, get, isNil } from 'lodash';

import { navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ProfileEntityName from 'components/entity/entity-name/ProfileEntityName';
import DeliverableTagPill from 'components/deliverable-tag-pill/DeliverableTagPill';
import SortableTable from 'components/sortable-table/SortableTable';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import DeliverablesMetricsInfo from 'modules/admin/components/deliverables/metrics-info/DeliverablesMetricsInfo';
import MetricsTempFBInfo from 'modules/campaigns/components/campaign-report/metrics-temp-fb/info/MetricsTempFBInfo';

import {
  deleteAdditionalDeliverable,
  updateDeliverableFBDataEnabled
} from 'modules/campaigns/components/campaign-report/metrics/reducers/campaign-report-metrics';
import {
  createDeliverable,
  deleteDeliverable
} from '../../../reducers/admin-additional-deliverable';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';
import { insertIf } from 'utils/list';
import {
  DELIVERABLE_ICONS,
  DELIVERABLE_TYPES_CHANNELS_MAP,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  getAdditionalDeliverablePossibleTaskTypes,
  isDeliverableNew
} from 'utils/deliverables';

import type {
  AdminDeliverable,
  AdminInfluencerPreview,
  Deliverable,
  DeliverableType,
  DeliverableUploadedEventLabel,
  LocationWindow,
  SortableTableColumnType,
  SortableTableSort
} from 'types';

applyTranslations(require('./deliverables-metrics-table.i18n.json'));

type NumberOfDeliverableObj = {
  story?: number,
  post?: number,
  reel?: number,
  video?: number,
  storyTitle?: string,
  postTitle?: string,
  reelTitle?: string,
  videoTitle?: string
}

type OwnProps = {
  location: LocationWindow,
  selectedInfluencer: AdminInfluencerPreview | undefined | null,
  deliverablesMetrics: Array<AdminDeliverable>,
  deliverableUploadedEventLabel: DeliverableUploadedEventLabel,
  isFullscreen?: boolean,
  campaignId?: number,
  isCampaignCA2?: boolean,
  renderFBDataBanner: () => React.ReactNode,
  onUpdate: () => void
};

type StateProps = {
  additionalDeliverableId: number | null,
  deliverableTypes: Array<DeliverableType>
};

type DispatchProps = {
  dispatchCreateDeliverable: (influencer: AdminInfluencerPreview, campaignId: number, isCampaignCA2: boolean) => Promise<void>,
  dispatchDiscardDeliverable: () => Promise<void>,
  dispatchUpdateFBDataEnabled: (deliverableId: number, dataEnabled: boolean) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message: string,
      onConfirm: () => void,
      onCancel: () => void,
      confirmButtonLabel: string
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchDeleteAdditionalDeliverable: (b: number, a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const DEFAULT_SORTING = 'updated_date.desc';

const getColumnsWidth = (isFullscreen) => ({
  updatedDate: !isFullscreen ? 150 : null,
  type: !isFullscreen ? 140 : null,
  tag: !isFullscreen ? 140 : null,
  creatorName: !isFullscreen ? 150 : null,
  channel: !isFullscreen ? 155 : null,
  analyticsCount: !isFullscreen ? 70 : null,
  mismatch: !isFullscreen ? 100 : null,
  taskName: !isFullscreen ? 280 : null
});

const getColumns = (isFullscreen, deliverableMetrics): Array<SortableTableColumnType> => {
  const columnsWidth = getColumnsWidth(isFullscreen);
  const hasTags = deliverableMetrics.some((d: AdminDeliverable) => !isNil(d.tag));

  return [
    {
      title: __t('deliverables_metrics_table.column.date'),
      key: 'updated_date',
      width: columnsWidth.updatedDate,
      sort: () => 0,
      render: (col) => col.updatedAt ? getDisplayDate(col.updatedAt) : '', // eslint-disable-line no-confusing-arrow
      initialSortingDirection: 'descending'
    },
    {
      title: __t('deliverables_metrics_table.column.task_name'),
      key: 'task_name',
      width: columnsWidth.taskName,
      sort: () => 0,
      render: (col) => (
        <div className="admin-deliverables-metrics-table__task-name">
          {col.name}
        </div>
      )
    },
    {
      title: __t('deliverables_metrics_table.column.type'),
      key: 'type',
      width: columnsWidth.type,
      sort: () => 0,
      render: (col) => (
        <div className="admin-deliverables-metrics-table__type">
          <SVGDynamicIcon
            name={`${DELIVERABLE_ICONS[camelCase(col.deliverableType)]}`}
            width={16}
            height={16}
            customColor="grey"
          />
          {
            col.additional ? (
              <Tooltip message={__t(`deliverables_metrics_table.additional_${col.deliverableType}`)}>
                <div className="admin-deliverables-metrics-table__type-name">
                  {__t(`deliverables_metrics_table.type_${col.deliverableType}`)}*
                  {col.deliverableType.includes('regram') && '*'}
                </div>
              </Tooltip>
            ) : (
              <div className="admin-deliverables-metrics-table__type-name">
                {__t(`deliverables_metrics_table.type_${col.deliverableType}`)}
              </div>
            )
          }
        </div>
      )
    },
    ...insertIf(hasTags,
      {
        title: __t('deliverables_metrics_table.column.tag'),
        key: 'tag',
        width: columnsWidth.tag,
        sort: () => 0,
        render: (col) => (
          !isNil(col.tag) && (
            <div className="admin-deliverables-metrics-table__tag">
              <DeliverableTagPill
                name={col.tag.name}
                color={col.tag.color}
                wide="long"
              />
            </div>
          )
        )
      }
    ),
    {
      title: __t('deliverables_metrics_table.column.influencer'),
      key: 'creator_name',
      width: columnsWidth.creatorName,
      sort: () => 0,
      render: (col) => (
        <div className="admin-deliverables-metrics-table__name">
          <ProfileEntityName
            profile={col.creatorProfile}
            openInNewTab
            size="small"
          />
        </div>
      )
    },
    {
      title: __t('deliverables_metrics_table.column.channel'),
      key: 'channel',
      width: columnsWidth.channel,
      render: (col) => {
        const channel = DELIVERABLE_TYPES_CHANNELS_MAP[col.deliverableType] || 'instagram';
        const channelName = get(col, `creatorProfile.channels.${channel}.username`);

        return channelName ? `@${channelName}` : '';
      }
    },
    {
      title: __t('deliverables_metrics_table.column.analytics'),
      titleTooltipMsg: __t('deliverables_metrics_table.column.analytics_tooltip'),
      key: 'analytics_count',
      width: columnsWidth.analyticsCount,
      align: 'center',
      sort: () => 0,
      render: col => {
        const analyticsCount = col.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY ?
          (col.postAttachments || []).filter(attachment => attachment.meta?.type === 'analytics').length : 1;
        return col.postAttachments.length ? analyticsCount : 0;
      }
    },
    {
      title: __t('deliverables_metrics_table.column.mismatch'),
      key: 'mismatch',
      width: columnsWidth.mismatch,
      align: 'center',
      render: col => {
        if (isDeliverableNew(col.id)) {
          return '-';
        }
        return col.isAssetMismatch ? 'yes' : 'no';
      }
    }
  ];
};

class DeliverablesMetricsTable extends React.PureComponent<Props> {
  static defaultProps = {
    isFullscreen: true
  };

  componentDidMount() {
    this.handleDiscardAdditionalDeliverable();
  }

  handleSortChanged = (
    {
      sortingKey,
      ascending
    }: SortableTableSort
  ): void => {
    const { location } = this.props;

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, {
      sort: encodeURIComponent(`${sortingKey}.${ascending ? 'asc' : 'desc'}`)
    })));
  };

  handleGetRowStatus = (item: any): string => {
    switch (item.insightsCompletenessStatus) {
      case 'missing':
        return 'error';
      case 'incomplete':
        return 'warning';
      default:
        return 'success';
    }
  };

  handleDataToggle = (
    deliverableId: number
  ): void => {
    const { deliverablesMetrics, dispatchUpdateFBDataEnabled } = this.props;
    const foundDeliverable = deliverablesMetrics.find(d => d.id === deliverableId);

    if (foundDeliverable) {
      dispatchUpdateFBDataEnabled(foundDeliverable.id, !foundDeliverable.isFacebookDataEnabled);
    }
  };

  handleAddAdditionalDeliverable = () => {
    const { selectedInfluencer, dispatchCreateDeliverable, campaignId, location, isCampaignCA2 } = this.props;

    const campaignIdToUse = campaignId ?? Number(location?.query?.campaignId);

    if (!selectedInfluencer || !campaignIdToUse) {
      return;
    }

    dispatchCreateDeliverable(selectedInfluencer, campaignIdToUse, isCampaignCA2 ?? false);
  };

  handleDiscardAdditionalDeliverable = () => {
    const { dispatchDiscardDeliverable } = this.props;
    dispatchDiscardDeliverable();
  };

  // Delete already an existed additional deliverable from DB
  handleDeleteDeliverable = (offerId: number, deliverableId: number) => {
    const { dispatchDeleteAdditionalDeliverable,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal,
      onUpdate } = this.props;

    dispatchShowConfirmationModal({
      title: __t('deliverables_metrics_table.deliverable_delete_confirmation.title'),
      message: __t('deliverables_metrics_table.deliverable_delete_confirmation.message'),
      confirmButtonLabel: __t('deliverables_metrics_table.deliverable_delete_confirmation.button_confirm'),
      onConfirm: () => dispatchDeleteAdditionalDeliverable(offerId, deliverableId).then(() => {
        onUpdate();
        dispatchHideConfirmationModal();
      }),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  isCollapsible = () => true;

  getTitle = (): string => {
    const { deliverablesMetrics } = this.props;
    /* eslint-disable no-param-reassign */
    const numOfDeliverable = deliverablesMetrics.reduce<NumberOfDeliverableObj>((numOfDeliverableByType, deliverablesMetric) => {
      if ([DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY, DELIVERABLE_TYPE_INSTAGRAM_STORY].includes(deliverablesMetric.deliverableType)) {
        numOfDeliverableByType.story = numOfDeliverableByType.story ? numOfDeliverableByType.story + 1 : 1;
        numOfDeliverableByType.storyTitle = `${numOfDeliverableByType.story}
        ${__t('deliverables_metrics_table.story_label', { withPlural: numOfDeliverableByType.story })}`;
      } else if ([DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_POST].includes(deliverablesMetric.deliverableType)) {
        numOfDeliverableByType.post = numOfDeliverableByType.post ? numOfDeliverableByType.post + 1 : 1;
        numOfDeliverableByType.postTitle = `${numOfDeliverableByType.post}
        ${__t('deliverables_metrics_table.post_label', { withPlural: numOfDeliverableByType.post })}`;
      } else if ([DELIVERABLE_TYPE_INSTAGRAM_REEL].includes(deliverablesMetric.deliverableType)) {
        numOfDeliverableByType.reel = numOfDeliverableByType.reel ? numOfDeliverableByType.reel + 1 : 1;
        numOfDeliverableByType.reelTitle = `${numOfDeliverableByType.reel}
        ${__t('deliverables_metrics_table.reel_label', { withPlural: numOfDeliverableByType.reel })}`;
      } else if ([DELIVERABLE_TYPE_TIKTOK_VIDEO].includes(deliverablesMetric.deliverableType)) {
        numOfDeliverableByType.video = numOfDeliverableByType.video ? numOfDeliverableByType.video + 1 : 1;
        numOfDeliverableByType.videoTitle = `${numOfDeliverableByType.video}
        ${__t('deliverables_metrics_table.video_label', { withPlural: numOfDeliverableByType.video })}`;
      }

      return numOfDeliverableByType;
    }, {});
    /* eslint-disable no-param-reassign */

    return [numOfDeliverable.postTitle,
      numOfDeliverable.storyTitle,
      numOfDeliverable.reelTitle,
      numOfDeliverable.videoTitle]
      .filter(title => Boolean(title))
      .join(', ')
      .replace(/,([^,]*)$/, ' and $1');
  };

  renderCollapsedContent = (
    deliverable: Deliverable
  ) => {
    const {
      isFullscreen,
      deliverableUploadedEventLabel,
      deliverableTypes,
      onUpdate
    } = this.props;

    const hasActiveFBConnection = Boolean(deliverable?.isFacebookDataEnabled);

    if (hasActiveFBConnection && !(deliverable as AdminDeliverable).additional) {
      return (
        <MetricsTempFBInfo
          key={`deliverables-metrics-table--deliverable-${deliverable.id}`}
          deliverable={deliverable as AdminDeliverable}
          hasActiveFbConnection={hasActiveFBConnection}
          onDataToggle={this.handleDataToggle}
        />
      );
    }

    return (
      <React.Fragment>
        <DeliverablesMetricsInfo
          hasActiveFBConnection={hasActiveFBConnection}
          deliverablesMetricsItem={deliverable}
          onDelete={this.handleDeleteDeliverable}
          isFullscreen={isFullscreen}
          deliverableUploadedEventLabel={deliverableUploadedEventLabel}
          deliverableTypes={deliverableTypes}
          onUpdate={() => onUpdate()}
          useFacebookDataLayout={hasActiveFBConnection}
        />
        {
          hasActiveFBConnection && (
            <MetricsTempFBInfo
              key={`deliverables-metrics-table--deliverable-${deliverable.id}`}
              deliverable={deliverable as AdminDeliverable}
              hasActiveFbConnection={hasActiveFBConnection}
              onDataToggle={this.handleDataToggle}
            />
          )
        }
      </React.Fragment>
    );
  };

  renderAddAdditionalContentButton = (): JSX.Element | null => {
    const { selectedInfluencer, deliverableTypes } = this.props;

    if (!selectedInfluencer || deliverableTypes.length === 0) {
      return null;
    }

    return (
      <ActionButton
        color="secondary"
        className="admin-deliverables-metrics-table__button"
        onClick={this.handleAddAdditionalDeliverable}
      >
        {__t('deliverables_metrics_table.button_add_content')}
      </ActionButton>
    );
  };

  render() {
    const {
      additionalDeliverableId,
      deliverablesMetrics,
      location,
      isFullscreen,
      renderFBDataBanner
    } = this.props;

    const sort = encodeURIComponent(get(location, 'query.sort', DEFAULT_SORTING)).split('.');
    const title = this.getTitle();

    // had to make custom <PageSection> due to where FB data banner needs to go
    return (
      <div className="admin-deliverables-metrics-table__page-section">
        <div className="admin-deliverables-metrics-table__page-section-heading-top">
          <div className="admin-deliverables-metrics-table__page-section-heading">
            {title}
          </div>
          <div>
            {this.renderAddAdditionalContentButton()}
          </div>
        </div>
        {renderFBDataBanner()}
        <div className="admin-deliverables-metrics-table__page-section-section">
          <SortableTable
            data={deliverablesMetrics.filter(isDefined)}
            noScroll
            noSticky
            expandedRows={additionalDeliverableId ? [additionalDeliverableId] : undefined}
            columns={getColumns(isFullscreen, deliverablesMetrics)}
            defaultSortingKey={sort[0]}
            defaultSortingDirection={sort[1] === 'asc' ? 'ascending' : 'descending'}
            customClass="admin-deliverables-metrics-table__table"
            getRowStatus={this.handleGetRowStatus}
            onSortChanged={this.handleSortChanged}
            isCollapsible={this.isCollapsible}
            renderCollapsedContent={this.renderCollapsedContent}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  campaignReportingMetrics,
  campaignDeliverables
}, props: Props): StateProps => {
  const influencerCampaignDeliverables = campaignDeliverables.groupedDeliverables
    .find(deliverables => deliverables.creatorProfile.id === props.selectedInfluencer?.id);

  return {
    additionalDeliverableId: campaignReportingMetrics.activeDeliverableId,
    deliverableTypes: getAdditionalDeliverablePossibleTaskTypes(
      influencerCampaignDeliverables?.deliverables || []
    )
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCreateDeliverable: (influencer, campaignId, isCampaignCA2) => dispatch(createDeliverable(influencer, campaignId, isCampaignCA2)),
  dispatchDiscardDeliverable: () => dispatch(deleteDeliverable()),
  dispatchUpdateFBDataEnabled: (deliverableId: number, dataEnabled: boolean) => (
    dispatch(updateDeliverableFBDataEnabled(deliverableId, dataEnabled))
  ),
  dispatchShowConfirmationModal: (params: {
    message: string
  }) => dispatch(showConfirmModal(params)),

  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchDeleteAdditionalDeliverable: (offerId, deliverableId) => dispatch(deleteAdditionalDeliverable(offerId, deliverableId))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverablesMetricsTable);
